'use client';

import React from 'react';
import { useWebSocket } from '@/shared/api/websocket';
import { ConnectionStatus } from '@/shared/api/websocket';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { WifiOff, Loader2 } from 'lucide-react';

export function ConnectionStatusIndicator() {
  const { status } = useWebSocket();

  const getStatusColor = () => {
    switch (status) {
      case ConnectionStatus.CONNECTED:
        return 'bg-primary';
      case ConnectionStatus.CONNECTING:
        return 'bg-accent';
      case ConnectionStatus.DISCONNECTED:
      case ConnectionStatus.FAILED:
      case ConnectionStatus.UNAVAILABLE:
        return 'bg-destructive';
      default:
        return 'bg-muted';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case ConnectionStatus.CONNECTED:
        return 'Connected';
      case ConnectionStatus.CONNECTING:
        return 'Connecting...';
      case ConnectionStatus.DISCONNECTED:
        return 'Disconnected';
      case ConnectionStatus.FAILED:
        return 'Connection Failed';
      case ConnectionStatus.UNAVAILABLE:
        return 'Unavailable';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="flex items-center gap-2 text-form-help">
      <div className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
      <span className="hidden sm:inline text-muted-foreground">
        {getStatusText()}
      </span>
    </div>
  );
}

export function ConnectionStatusBadge() {
  const { status, isConnected } = useWebSocket();

  if (isConnected) return null;

  const getIcon = () => {
    switch (status) {
      case ConnectionStatus.CONNECTING:
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case ConnectionStatus.DISCONNECTED:
      case ConnectionStatus.FAILED:
      case ConnectionStatus.UNAVAILABLE:
        return <WifiOff className="h-4 w-4" />;
      default:
        return <WifiOff className="h-4 w-4" />;
    }
  };

  const getVariant = (): 'default' | 'destructive' => {
    switch (status) {
      case ConnectionStatus.CONNECTING:
        return 'default';
      case ConnectionStatus.DISCONNECTED:
      case ConnectionStatus.FAILED:
      case ConnectionStatus.UNAVAILABLE:
        return 'destructive';
      default:
        return 'default';
    }
  };

  const getBadgeText = () => {
    switch (status) {
      case ConnectionStatus.CONNECTING:
        return 'Reconnecting...';
      case ConnectionStatus.DISCONNECTED:
        return 'Offline - Real-time updates paused';
      case ConnectionStatus.FAILED:
        return 'Connection failed - Retrying...';
      case ConnectionStatus.UNAVAILABLE:
        return 'Connection unavailable';
      default:
        return 'Connection issue';
    }
  };

  return (
    <Alert variant={getVariant()} className="border-0 bg-opacity-10">
      <div className="flex items-center gap-2">
        {getIcon()}
        <AlertDescription className="text-card-meta">
          {getBadgeText()}
        </AlertDescription>
      </div>
    </Alert>
  );
}
